import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, X, Search } from 'lucide-react';
import { ADMIN_NAV, findNavItem, navPath } from './adminNav';

export default function AdminNavSheet() {
  const { groupId, itemId } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const current = findNavItem(groupId, itemId);
  const needle = filter.trim().toLowerCase();

  const groups = ADMIN_NAV
    .map((group) => ({
      ...group,
      items: needle
        ? group.items.filter((i) =>
            i.label.toLowerCase().includes(needle) || group.label.toLowerCase().includes(needle))
        : group.items,
    }))
    .filter((group) => group.items.length > 0);

  const go = (gId, iId) => {
    navigate(navPath(gId, iId));
    setOpen(false);
    setFilter('');
  };

  return (
    <div className="md:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 mb-4"
      >
        <span className="min-w-0 text-left">
          <span className="block font-rasa text-xs text-gray-400 dark:text-gray-500 truncate">
            {current?.group.label || 'Admin'}
          </span>
          <span className="block font-rasa text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
            {current?.item.label || 'Select a section'}
          </span>
        </span>
        <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col" role="dialog" aria-modal="true" aria-label="Admin sections">
          <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <Search size={16} className="text-gray-400 flex-shrink-0" />
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter sections"
              className="flex-1 min-w-0 bg-transparent font-rasa text-sm outline-none dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => { setOpen(false); setFilter(''); }}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {groups.length === 0 && (
              <p className="font-rasa text-sm text-gray-500 dark:text-gray-400 px-2 py-4">No section matches &ldquo;{filter}&rdquo;.</p>
            )}
            {groups.map((group) => (
              <div key={group.id} className="mb-4">
                <p className="px-2 pb-1 font-rasa text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = group.id === groupId && item.id === itemId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => go(group.id, item.id)}
                      className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left font-rasa text-sm ${
                        active
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 font-medium'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
